import {
  selectUserGuid,
  selectGroupGuid,
  useAppSelector,
} from '../../../slices/commonSelectors';
import { useActualIncidentId } from './commonHooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { apiPrefix } from 'constants/apiConfig';
import { toast } from 'react-toastify';
import { invalidateReviewQueue } from 'components/ReviewQueue/reviewQueueKey';
import type { ActionAttachment } from '../actionTicket/actionTicketModel';

export type ResourceRequestStatus =
  | 'draft'
  | 'partially_signed'
  | 'awaiting_approval'
  | 'awaiting_review'
  | 'awaiting_order'
  | 'awaiting_payment'
  | 'awaiting_incident_commander'
  | 'awaiting_final'
  | 'completed'
  | 'cancelled';

export interface ResourceRequestItem {
  id?: string;
  name?: string;
  kind: string;
  type: string;
  unit: string;
  quantity: number;
  priority?: string;
  description?: string;
  requested_reporting_location?: string;
  requested_reporting_location_id?: string;
  requested_reporting_location_geolocation?: {
    lat: number;
    lng: number;
  } | null;
  requested_reporting_datetime?: string;
  requested_demob_datetime?: string;
  order_number?: string;
  eta?: string;
  cost?: number;
  resource_set_id?: string;
  /** Linked ICS 213RR editor: this row's Attach is the owning asset. */
  attached_to_asset?: boolean;
  /** Asset that can't be removed from this row's Attach. */
  locked_attach_resource_set_id?: string;
  arrival_datetime_actual?: string;
  source_resource_site_id?: string;
  destination_resource_site_id?: string;
  owning_organization?: string;
  status: 'pending' | 'approved' | 'in_progress' | 'fulfilled' | 'cancelled';
}

export type ResourceRequest = {
  id: UUID;
  resource_request_id: UUID;
  group_guid: UUID;
  incident_id: UUID;
  user_guid: UUID;
  version: number;
  request_number: string;
  name: string;
  attachments?: ActionAttachment[];
  supply_sources?: string;
  substitutes?: string;
  requestor_name?: string;
  requestor_position?: string;
  requestor_signature?: string;
  requestor_signature_timestamp?: Date;
  section_chief_name?: string;
  section_chief_position?: string;
  section_chief_signature?: string;
  section_chief_signature_timestamp?: Date;
  section_chief_comments?: string;
  resl_position?: string;
  resl_tac?: boolean;
  resl_personnel?: boolean;
  resl_available?: boolean;
  resl_not_available?: boolean;
  resl_name?: string;
  resl_signature?: string;
  resl_signature_timestamp?: Date;
  resl_comments?: string;
  logistics_order_number?: string;
  logistics_eta?: Date;
  logistics_cost?: number;
  supplier_name?: string;
  supplier_phone?: string;
  supplier_fax?: string;
  supplier_email?: string;
  logistics_notes?: string;
  logistics_comments?: string;
  logistics_order_placed_by?: string;
  logistics_order_placed_by_other?: string;
  logistics_name?: string;
  logistics_position?: string;
  logistics_signature?: string;
  logistics_signature_timestamp?: Date;
  logistics_items?: ResourceRequestItem[][];
  source_location_id?: string;
  source_location_geolocation?: {
    lat: number;
    lng: number;
  } | null;
  finance_name?: string;
  finance_position?: string;
  finance_signature?: string;
  finance_signature_timestamp?: Date;
  finance_comments?: string;
  incident_commander_name?: string;
  incident_commander_position?: string;
  incident_commander_signature?: string;
  incident_commander_signature_timestamp?: Date;
  incident_commander_comments?: string;
  final_name?: string;
  final_position?: string;
  final_signature?: string;
  final_signature_timestamp?: Date;
  final_comments?: string;
  items?: ResourceRequestItem[];
  status: ResourceRequestStatus;
  timestamp: Date;
  archived: boolean;
  work_assignment_ids: UUID[] | null;
  capability_enabled?: string;
  /** Asset whose Linked ICS 213RR this request was created from. */
  source_resource_set_id?: UUID | null;
  /** Present on the group list, which spans incident workspaces. */
  incident_name?: string;
};

export const keys = {
  base: 'incident_resource_requests',
  get all() {
    return [keys.base];
  },
  get: (incidentId: string) => [keys.base, incidentId],
  detail: (incidentId: string, requestId: string) => [
    ...keys.get(incidentId),
    requestId,
  ],
};

export const useIncidentResourceRequests = (
  includeArchived = false,
  options?: { enabled?: boolean }
) => {
  const incidentId = useActualIncidentId();
  return useQuery({
    queryKey: [...keys.get(incidentId), includeArchived],
    enabled: (options?.enabled ?? true) && Boolean(incidentId),
    queryFn: async () => {
      const resp = await axios.get<ResourceRequest[]>(
        `${apiPrefix}/dice/ics/resource-requests/${incidentId}`,
        {
          params: { includeArchived },
        }
      );
      return resp.data;
    },
  });
};

export const useGroupResourceRequests = (options?: { enabled?: boolean }) => {
  const groupGuid = useAppSelector(selectGroupGuid);
  return useQuery({
    queryKey: [...keys.all, 'group', groupGuid],
    enabled: (options?.enabled ?? true) && Boolean(groupGuid),
    queryFn: async () => {
      const resp = await axios.get<ResourceRequest[]>(
        `${apiPrefix}/dice/ics/resource-requests`,
        { params: { groupGuid } }
      );
      return resp.data;
    },
  });
};

export const useResourceRequest = (requestId: string) => {
  const incidentId = useActualIncidentId();
  return useQuery({
    queryKey: keys.detail(incidentId, requestId),
    queryFn: async () => {
      const resp = await axios.get<ResourceRequest>(
        `${apiPrefix}/dice/ics/resource-requests/${incidentId}/${requestId}`
      );
      return resp.data;
    },
    enabled: !!requestId,
  });
};

type CreateResourceRequestData = Omit<
  ResourceRequest,
  | 'id'
  | 'resource_request_id'
  | 'user_guid'
  | 'version'
  | 'timestamp'
  | 'request_number'
>;

export const useCreateResourceRequest = () => {
  const user_guid = useAppSelector(selectUserGuid);
  const group_guid = useAppSelector(selectGroupGuid);
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      data: Partial<CreateResourceRequestData> & {
        request_number?: string;
        timestamp?: string;
      }
    ) => {
      const response = await axios.post(
        `${apiPrefix}/dice/ics/resource-requests/${incidentId}`,
        {
          ...data,
          group_guid,
          user_guid,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.get(incidentId),
      });
      toast.success('Resource request created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || error.message);
    },
  });
};

/**
 * Creates an unsigned draft 213RR in the current incident from an asset's saved
 * Linked ICS 213RR.
 */
export const useCreateResourceRequestFromResourceSet = () => {
  const group_guid = useAppSelector(selectGroupGuid);
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (resourceSetId: string) => {
      const response = await axios.post<ResourceRequest>(
        `${apiPrefix}/dice/ics/resource-requests/${incidentId}/from-resource-set/${resourceSetId}`,
        { group_guid }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.get(incidentId),
      });
      toast.success('ICS 213RR created from the linked 213RR');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || error.message);
    },
  });
};

type UpdateResourceRequestData = Partial<ResourceRequest>;

export const useUpdateResourceRequest = () => {
  const user_guid = useAppSelector(selectUserGuid);
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      requestId,
      data,
    }: {
      requestId: string;
      data: UpdateResourceRequestData;
    }) => {
      const response = await axios.put(
        `${apiPrefix}/dice/ics/resource-requests/${incidentId}/${requestId}`,
        { ...data, user_guid }
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: keys.get(incidentId),
      });
      // Also invalidate resource sets in case status changed
      queryClient.invalidateQueries({
        queryKey: ['resource_sets'],
      });
      // If status changed to completed, invalidate resources in incident
      const newStatus = variables.data.status;
      if (
        newStatus === 'completed' ||
        (newStatus === 'awaiting_payment' && variables.data.finance_signature)
      ) {
        queryClient.invalidateQueries({
          queryKey: ['resources-in-incident', incidentId],
          exact: false,
        });
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || error.message);
    },
  });
};

export const useApproveResourceRequest = () => {
  const user_guid = useAppSelector(selectUserGuid);
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      requestId,
      approvalData,
    }: {
      requestId: string;
      approvalData: any;
    }) => {
      const response = await axios.post(
        `${apiPrefix}/dice/ics/resource-requests/${incidentId}/${requestId}/approve`,
        { ...approvalData, user_guid }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.get(incidentId),
      });
      // Also invalidate resource sets to update need calculations
      queryClient.invalidateQueries({
        queryKey: ['resource_sets'],
      });
      // Invalidate resources in incident since backend creates them automatically
      queryClient.invalidateQueries({
        queryKey: ['resources-in-incident', incidentId],
        exact: false,
      });
      invalidateReviewQueue(queryClient);
      toast.success('Resource request approved successfully');
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.publicMessage ||
          error.response?.data?.error ||
          error.message ||
          'An error occurred'
      );
    },
  });
};
export const useDenyResourceRequest = () => {
  const user_guid = useAppSelector(selectUserGuid);
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      requestId,
      denyData,
    }: {
      requestId: string;
      denyData: any;
    }) => {
      const response = await axios.post(
        `${apiPrefix}/dice/ics/resource-requests/${incidentId}/${requestId}/deny`,
        { ...denyData, user_guid }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.get(incidentId),
      });
      // Also invalidate resource sets to update need calculations
      queryClient.invalidateQueries({
        queryKey: ['resource_sets'],
      });
      invalidateReviewQueue(queryClient);
      toast.success('Resource request rejected successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || error.message);
    },
  });
};
// Legacy hook for backward compatibility
export const useUpdateIncidentResourceRequest = useUpdateResourceRequest;
